import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)

  // Simple initialization - no complex logic
  useEffect(() => {
    
    const initializeAuth = async () => {
      try {
        // Check if this is a fresh app open (not a refresh)
        const hasAppOpenedFlag = sessionStorage.getItem('appOpened')
        
        if (!hasAppOpenedFlag) {
          // This is a fresh app open - clear all authentication data
          await supabase.auth.signOut()
          localStorage.removeItem('currentUser')
          sessionStorage.removeItem('currentUser')
          sessionStorage.setItem('appOpened', 'true')
          setUser(null)
        } else {
          // This is a refresh or app already opened - check for existing session
          const { data: { session } } = await supabase.auth.getSession()
          
          if (session?.user) {
            
            // Fetch user role from user_profiles table
            const { data: userProfile, error: profileError } = await supabase
              .from('user_profiles')
              .select('role')
              .eq('id', session.user.id)
              .single()
            
            const userRole = userProfile?.role || session.user.user_metadata?.role || 'viewer'
            
            setUser(session.user)
            
            // Store user info in localStorage for simple auth checks
            localStorage.setItem('currentUser', JSON.stringify({
              id: session.user.id,
              email: session.user.email,
              role: userRole
            }))
          } else {
            // Clear any existing data
            localStorage.removeItem('currentUser')
            sessionStorage.removeItem('currentUser')
            setUser(null)
          }
        }
        
        setLoading(false)
        setIsInitialized(true)
        
      } catch (error) {
        console.error('Error during auth initialization:', error)
        // Clear data on error
        localStorage.removeItem('currentUser')
        sessionStorage.removeItem('currentUser')
        setUser(null)
        setLoading(false)
        setIsInitialized(true)
      }
    }

    initializeAuth()

    // Set up simple auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      
      if (event === 'SIGNED_IN' && session?.user) {
        setUser(session.user)
        
        // Only set localStorage if it's not already set (to avoid race condition with signIn function)
        const existingUser = localStorage.getItem('currentUser')
        if (!existingUser) {
          // Fetch user role from user_profiles table and wait for it
          const fetchRoleAndSetStorage = async () => {
            try {
              const { data: userProfile, error: profileError } = await supabase
                .from('user_profiles')
                .select('role')
                .eq('id', session.user.id)
                .single()
              
              const userRole = userProfile?.role || session.user.user_metadata?.role || 'viewer'
              localStorage.setItem('currentUser', JSON.stringify({
                id: session.user.id,
                email: session.user.email,
                role: userRole
              }))
            } catch (error) {
              console.error('Error fetching user role in auth state change:', error)
              // Fallback to user metadata role
              const userRole = session.user.user_metadata?.role || 'viewer'
              localStorage.setItem('currentUser', JSON.stringify({
                id: session.user.id,
                email: session.user.email,
                role: userRole
              }))
            }
          }
          
          fetchRoleAndSetStorage()
        } else {
          }
        
        // Set the app opened flag to prevent logout on refresh
        sessionStorage.setItem('appOpened', 'true')
      } else if (event === 'SIGNED_OUT') {
        setUser(null)
        localStorage.removeItem('currentUser')
        sessionStorage.removeItem('currentUser')
        sessionStorage.removeItem('appOpened')
      }
    })

    return () => subscription?.unsubscribe()
  }, [])

  // Simplified approach - only clear appOpened flag on manual logout
  // The sessionStorage will naturally be cleared when the tab is closed
  // This ensures fresh app opens require login but refreshes preserve login

  // Simple sign in - no complex profile fetching
  const signIn = async (email, password) => {
    try {
      setLoading(true)
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      })

      if (error) throw error
      
      if (data?.user) {
        setUser(data.user)
        
        // Fetch user role from user_profiles table
        const { data: userProfile, error: profileError } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', data.user.id)
          .single()
        
        
        // Prioritize database role over user metadata role
        let userRole = 'viewer'
        if (userProfile?.role) {
          userRole = userProfile.role
        } else if (data.user.user_metadata?.role) {
          userRole = data.user.user_metadata.role
        } else {
        }
        
        // Store user info in localStorage for simple auth checks
        localStorage.setItem('currentUser', JSON.stringify({
          id: data.user.id,
          email: data.user.email,
          role: userRole
        }))
        
        // Set the app opened flag to prevent logout on refresh
        sessionStorage.setItem('appOpened', 'true')
      }
      
      return { data, error: null }
    } catch (error) {
      console.error('Error signing in:', error)
      return { data: null, error }
    } finally {
      setLoading(false)
    }
  }

  // Simple sign out
  const signOut = async () => {
    try {
      setLoading(true)
      await supabase?.auth?.signOut()
      
      setUser(null)
      localStorage.removeItem('currentUser')
      sessionStorage.removeItem('currentUser')
      sessionStorage.removeItem('appOpened') // Clear app opened flag
      
      return { error: null }
    } catch (error) {
      console.error('Error signing out:', error)
      return { error }
    } finally {
      setLoading(false)
    }
  }

  // Simple value object - only essential functions
  const value = {
    user,
    userProfile: null, // Always null to prevent profile-related loops
    loading,
    signIn,
    signOut,
    isInitialized
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
